import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../styles/CustomerPromise.css';

/*
  The shop's four promises, said once.

  They used to appear twice on the home page — as a compact icon strip
  under the planets ("Secure Payment · 100% secure checkout") and again
  a screen later inside About as "Our Customer Promise" ("Secure
  Shopping · SSL encrypted payments…"). Same four commitments, two
  different wordings, which reads as padding rather than reassurance.

  This keeps the strip's position, because trust signals belong beside
  the shopping, and the About block's specifics, because "insured,
  tamper-proof, tracked" is what actually answers the worry.
*/

const PROMISES = [
  {
    icon: '🔒',
    title: 'Secure Payment',
    lead: '100% secure checkout',
    detail:
      'Encrypted payments and several ways to pay. Your details are never shared.',
  },
  {
    icon: '📦',
    title: 'Fast Delivery',
    lead: '3–5 business days',
    detail:
      'Insured, tamper-proof packaging, tracked from our hands to yours.',
  },
  {
    icon: '↩️',
    title: 'Easy Returns',
    lead: '7-day return policy',
    detail:
      'Counted from the day it arrives. Unused, in its original packaging, with the certificate intact.',
    /* The conditions matter — custom-energized pieces can't come back
       at all, and a return needs approval first. Say enough here that
       the link is worth following, and let the policy page carry the
       rest. */
    link: { to: '/shipping-returns', label: 'Read the return policy' },
  },
  {
    icon: '💎',
    title: 'Quality Guarantee',
    lead: 'Lab certified gems',
    detail:
      'A lab certificate ships with every stone, and your money back if it is ever proven otherwise.',
  },
];

export default function CustomerPromise({ heading = 'Our Customer Promise' }) {
  return (
    /* The band carries the page gutter. This used to sit inside the
       planets section and borrow its padding; it is a section of the
       page in its own right now, so it brings its own. */
    <div className="promise-band">
      <motion.section
        className="promise"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.45 }}
        aria-label={heading}
      >
        <h3 className="promise-heading">{heading}</h3>

        <ul className="promise-grid">
          {PROMISES.map((promise) => (
            <li className="promise-item" key={promise.title}>
              <span className="promise-icon" aria-hidden="true">
                {promise.icon}
              </span>
              <h4>{promise.title}</h4>
              <p className="promise-lead">{promise.lead}</p>
              <p className="promise-detail">{promise.detail}</p>
              {promise.link && (
                <Link className="promise-link" to={promise.link.to}>
                  {promise.link.label}
                  <span aria-hidden="true"> →</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      </motion.section>
    </div>
  );
}
